import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  api, ApiError, errMsg, newIdemKey, type Account, type Category, type CounterpartyPage, type Impact, type MemoRule, type Nature, type Scope, type Suggestion, type Transaction, type TransactionResult,
} from "../api";
import { useEntities } from "../entity";
import { formatMoney, normalizeMoneyInput } from "../lib/money";
import { useMe } from "../session";
import { AllocationField, allocError, saveAllocations, type AllocValue } from "./AllocationField";
import { AttachmentsField, uploadPending } from "./Attachments";
import { Modal } from "./Modal";
import { ScopeDialog, useRepeat } from "./Recurrence";
import { Btn, Field, Input, Notice, Select } from "./ui";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

/** Impacto no saldo calculado pelo backend (a tela só exibe). */
export function ImpactList({ impacts }: { impacts: Impact[] }) {
  if (!impacts.length) return null;
  return (
    <div className="impact" data-impact>
      {impacts.map((i) => (
        <div key={i.accountId}>Saldo de <strong>{i.accountName}</strong>: <span className="old tabular">{formatMoney(i.before.decimal, i.currency)}</span> → <span className="new tabular">{formatMoney(i.after.decimal, i.currency)}</span></div>
      ))}
    </div>
  );
}

type Learn = { saved: TransactionResult; proposal: string; existing: MemoRule | null };

/**
 * Lançamento (receita/despesa). Categoria é obrigatória em todos os lançamentos (DEC-056).
 * Memorização: a sugestão só preenche categoria/beneficiário-pagador quando o usuário clica em "Aplicar"; nunca mexe em valor ou data.
 */
export function TxForm({ kind: initialKind, tx, onClose }: { kind: Nature; tx: Transaction | null; onClose: () => void }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const { current } = useEntities();
  const entityId = tx?.entityId ?? current?.id ?? "";
  const [kind, setKind] = useState<Nature>(initialKind);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [cps, setCps] = useState<CounterpartyPage["items"]>([]);
  const [accountId, setAccountId] = useState(tx?.accountId ?? "");
  const [description, setDescription] = useState(tx?.description ?? "");
  const [amount, setAmount] = useState(tx ? tx.amount.decimal.replace(".", ",") : "");
  const [plannedDate, setPlannedDate] = useState(tx?.plannedDate ?? today());
  const [settled, setSettled] = useState(tx ? tx.status !== "planejado" : false);
  const [settledDate, setSettledDate] = useState(tx?.settledDate ?? today());
  const [categoryId, setCategoryId] = useState(tx?.categoryId ?? "");
  const [counterpartyId, setCounterpartyId] = useState(tx?.counterpartyId ?? "");
  const [notes, setNotes] = useState(tx?.notes ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [dup, setDup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sug, setSug] = useState<Suggestion["suggestion"]>(null);
  const [accepted, setAccepted] = useState<{ ruleId: string; categoryId: string | null } | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [saved, setSaved] = useState<TransactionResult | null>(null);
  const [learn, setLearn] = useState<Learn | null>(null);
  const [learnMsg, setLearnMsg] = useState<string | null>(null);
  const rep = useRepeat();
  const [askScope, setAskScope] = useState(false);
  const askRef = useRef(false); askRef.current = askScope;
  const closeForm = useCallback(() => { if (!askRef.current) onClose(); }, [onClose]);   // Esc com a pergunta aberta fecha só a pergunta
  const [pending, setPending] = useState<File[]>([]);
  const [alloc, setAlloc] = useState<AllocValue | null>(null);
  const [attachFail, setAttachFail] = useState<string[]>([]);
  const idem = useRef(newIdemKey());
  const reconciled = tx?.status === "conciliado";
  const lockedSettle = tx ? tx.status === "planejado" : false;   // efetivar um planejado é a ação "Marcar como pago/recebido"

  useEffect(() => {
    void Promise.all([
      api<Account[]>("GET", `/api/${realm}/accounts?entityId=${entityId}&status=ativa`),
      api<Category[]>("GET", `/api/${realm}/categories?status=ativa`),
    ]).then(([a, c]) => {
      setAccounts(a); setCats(c);
      if (!tx && a[0]) setAccountId((cur) => cur || a[0]!.id);
    }).catch((e) => setErr(errMsg(e)));
  }, [realm, entityId, tx]);
  // Beneficiários (despesa) e pagadores (receita) são listas separadas; quem é "cliente e fornecedor" aparece nas duas.
  useEffect(() => {
    void api<CounterpartyPage>("GET", `/api/${realm}/counterparties?status=ativa&limit=200&role=${kind === "despesa" ? "fornecedor" : "cliente"}`).then((p) => {
      let items = p.items;
      if (tx?.counterpartyId && !items.some((c) => c.id === tx.counterpartyId)) items = [...items, { id: tx.counterpartyId, name: tx.counterpartyName ?? "(cadastro atual)", document: null, status: "ativa", version: 0, inactivationReason: null }];
      setCps(items);
      setCounterpartyId((cur) => (cur && !items.some((c) => c.id === cur) ? "" : cur));
    }).catch((e) => setErr(errMsg(e)));
  }, [realm, kind, tx]);
  // a conta atual de um lançamento pode estar inativa: mantém a opção
  useEffect(() => {
    if (tx && accounts.length && !accounts.some((a) => a.id === tx.accountId)) setAccounts((l) => [...l, { id: tx.accountId, name: tx.accountName, currency: tx.currency } as Account]);
  }, [tx, accounts]);

  const options = useMemo(() => {
    const byId = new Map(cats.map((c) => [c.id, c]));
    const path = (c: Category): string => (c.parentId && byId.get(c.parentId) ? `${path(byId.get(c.parentId)!)} › ${c.name}` : c.name);
    return cats.filter((c) => c.level >= 2 && c.nature === kind && !c.systemKey).map((c) => ({ id: c.id, label: path(c) })).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [cats, kind]);
  const selectedCurrency = accounts.find((a) => a.id === accountId)?.currency ?? tx?.currency ?? "BRL";

  // sugestão por descrição (somente leitura até o clique em "Aplicar")
  useEffect(() => {
    if (reconciled || description.trim().length < 3 || !entityId) { setSug(null); return; }
    const h = setTimeout(() => {
      void api<Suggestion>("GET", `/api/${realm}/memo/suggest?entityId=${entityId}&description=${encodeURIComponent(description)}`)
        .then((r) => setSug(r.suggestion)).catch(() => setSug(null));
    }, 350);
    return () => clearTimeout(h);
  }, [description, entityId, realm, reconciled]);

  const sugUsable = sug && dismissed !== sug.ruleId && (!sug.category || options.some((o) => o.id === sug.category!.id)) && (sug.category || sug.counterparty);
  const apply = () => {
    if (!sug) return;
    if (sug.category) setCategoryId(sug.category.id);
    if (sug.counterparty) setCounterpartyId(sug.counterparty.id);
    setAccepted({ ruleId: sug.ruleId, categoryId: sug.category?.id ?? null });
  };

  async function submit(e: FormEvent | null, confirmDuplicate = false, chosen?: Scope) {
    e?.preventDefault();
    setErr(null);
    const norm = normalizeMoneyInput(amount);
    if (norm === null || norm.startsWith("-") || /^0+(\.0+)?$/.test(norm)) return setErr("Informe um valor maior que zero, por exemplo 1.234,56.");
    if (!categoryId) return setErr("Escolha a categoria: ela é obrigatória em todos os lançamentos.");
    if (!accountId) return setErr("Escolha a conta.");
    if (!description.trim()) return setErr("Informe a descrição.");
    const rule = accepted && accepted.categoryId === categoryId ? accepted.ruleId : null;
    let repeat: Record<string, unknown> | undefined;
    if (!tx) { const b = rep.build(); if (b.error) return setErr(b.error); repeat = b.repeat; }
    const ae = allocError(alloc); if (ae) return setErr(ae);
    // ocorrência de série: a pergunta "somente esta / esta e as seguintes" é obrigatória antes de gravar
    if (tx?.seriesId && !chosen) { setAskScope(true); return; }
    setBusy(true);
    try {
      let r: TransactionResult;
      if (tx) {
        const body: Record<string, unknown> = { version: tx.version, description, categoryId, counterpartyId: counterpartyId || null, notes: notes.trim() || null, acceptedRuleId: rule };
        if (!reconciled) Object.assign(body, { accountId, kind, amount: norm, plannedDate, ...(settled && !lockedSettle ? { settledDate } : {}) });
        if (tx.seriesId && chosen === "following") body.scope = "following";
        r = await api<TransactionResult>("PATCH", `/api/${realm}/transactions/${tx.id}`, body);
      } else {
        if (confirmDuplicate) idem.current = newIdemKey();
        r = await api<TransactionResult>("POST", `/api/${realm}/transactions`, {
          accountId, kind, description, amount: norm, plannedDate: settled ? settledDate : plannedDate, ...(settled ? { settledDate } : {}),
          categoryId, counterpartyId: counterpartyId || null, notes: notes.trim() || null, acceptedRuleId: rule, ...(confirmDuplicate ? { confirmDuplicate: true } : {}), ...(repeat ? { repeat } : {}),
        }, idem.current);
      }
      if (!tx && pending.length) setAttachFail(await uploadPending(realm, "transaction", r.id, pending));
      if (alloc && !(r as { series?: unknown }).series) { try { await saveAllocations(realm, "transaction", r.id, alloc); } catch (x) { setAttachFail((f) => [...f, `Rateio não gravado: ${errMsg(x)}`]); } }
      setSaved(r);
      // Q7: aprender só com a autorização do usuário, depois de salvar
      const unchanged = accepted && accepted.categoryId === categoryId;
      if (!unchanged && (!tx || tx.categoryId !== categoryId)) {
        const s = await api<Suggestion>("GET", `/api/${realm}/memo/suggest?entityId=${entityId}&description=${encodeURIComponent(description)}`).catch(() => null);
        if (s && s.patternProposal && s.suggestion?.category?.id !== categoryId) {   // já existe regra que leva a esta categoria: nada a aprender
          let existing: MemoRule | null = null;
          if (s.suggestion) existing = (await api<MemoRule[]>("GET", `/api/${realm}/memo-rules?status=ativa`).catch(() => [])).find((m) => m.id === s.suggestion!.ruleId) ?? null;
          setLearn({ saved: r, proposal: s.patternProposal, existing });
        }
      }
    } catch (x) {
      if (x instanceof ApiError && x.code === "possible_duplicate") { setDup(true); setErr(null); }
      else setErr(errMsg(x));
    } finally { setBusy(false); setAskScope(false); }
  }

  if (saved) {
    return (
      <Modal title={tx ? "Lançamento atualizado" : "Lançamento registrado"} onClose={onClose}>
        <Notice kind="ok">{saved.status === "planejado" ? "Planejado" : "Confirmado"}: {saved.description} — {formatMoney(saved.amount.decimal, saved.currency)}.
          {saved.series && ` Foram criados ${saved.series.created} ${saved.series.kind === "parcelada" ? "parcelamentos" : "lançamentos"} de uma vez${saved.series.kind === "fixa" && saved.installmentTotal === null ? "; eles serão renovados automaticamente ao final do período" : ""}.`}
          {saved.affected ? ` Também foram alterados ${saved.affected} lançamentos seguintes.` : ""}</Notice>
        <ImpactList impacts={saved.impacts} />
        {attachFail.length > 0 && <Notice kind="error">O lançamento foi salvo, mas houve falha ao anexar: {attachFail.join(" ")} Abra “Editar” para anexar de novo.</Notice>}
        {learn && <LearnPrompt learn={learn} categoryId={categoryId} counterpartyId={counterpartyId || null} entityId={entityId} onDone={(m) => { setLearn(null); setLearnMsg(m ?? null); }} />}
        {learnMsg && <Notice kind="ok">{learnMsg}</Notice>}
        {!learn && <div className="modal-actions"><Btn onClick={onClose} data-tx-close>Concluir</Btn></div>}
      </Modal>
    );
  }

  return (
    <><Modal title={tx ? "Editar lançamento" : kind === "despesa" ? "Nova despesa" : "Nova receita"} onClose={closeForm} wide>
      <form onSubmit={(e) => void submit(e)} noValidate data-tx-form>
        {err && <Notice kind="error">{err}</Notice>}
        {dup && (
          <Notice kind="error">
            Já existe um lançamento igual (mesma conta, tipo, valor, data e descrição). Deseja lançar mesmo assim?
            <div className="row" style={{ marginTop: 8 }}>
              <Btn variant="danger" onClick={(e) => { setDup(false); void submit(e as unknown as FormEvent, true); }} data-dup-confirm>Lançar mesmo assim</Btn>
              <Btn variant="ghost" onClick={() => setDup(false)}>Voltar</Btn>
            </div>
          </Notice>
        )}
        {reconciled && <Notice>Lançamento conciliado: valor, datas, conta e tipo estão travados. Categoria, beneficiário/pagador e observações podem ser ajustadas.</Notice>}
        {!tx && (
          <div className="seg" role="group" aria-label="Tipo" style={{ marginBottom: 12 }}>
            {(["despesa", "receita"] as const).map((k) => <button type="button" key={k} aria-pressed={kind === k} onClick={() => { setKind(k); setCategoryId(""); }} data-kind={k}>{k === "despesa" ? "Despesa" : "Receita"}</button>)}
          </div>
        )}
        <Field label="Descrição" required><Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} required autoComplete="off" name="description" disabled={reconciled} /></Field>
        {sugUsable && sug && (
          <div className="suggest" data-suggestion>
            <span>Sugestão da memorização: {sug.category ? <strong>{sug.category.path}</strong> : null}{sug.category && sug.counterparty ? " · " : ""}{sug.counterparty ? <strong>{sug.counterparty.name}</strong> : null}</span>
            <Btn variant="ghost" onClick={apply} data-suggestion-apply>Aplicar</Btn>
            <Btn variant="ghost" onClick={() => setDismissed(sug.ruleId)} data-suggestion-ignore>Ignorar</Btn>
          </div>
        )}
        <div className="grid2">
          <Field label={`Valor (${selectedCurrency})`} required><Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" decimals={2} placeholder="0,00" name="amount" disabled={reconciled} /></Field>
          <Field label="Conta" required><Select value={accountId} onChange={(e) => setAccountId(e.target.value)} name="account" disabled={reconciled}>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}</Select></Field>
        </div>
        <div className="grid2">
          <Field label="Categoria" required>
            <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} name="category" required>
              <option value="">Selecione…</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </Select>
          </Field>
          <Field label={kind === "despesa" ? "Beneficiário" : "Pagador"}>
            <Select value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)} name="counterparty">
              <option value="">—</option>
              {cps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
        </div>
        {!tx && rep.fields({ norm: normalizeMoneyInput(amount), format: (v) => formatMoney(v.toFixed(2), selectedCurrency) })}
        {tx?.seriesId && <p className="rep-info" data-scope-box>{tx.seriesKind === "parcelada" ? `Parcela ${tx.installmentNo}/${tx.installmentTotal}` : tx.installmentNo ? `Lançamento fixo nº ${tx.installmentNo}${tx.installmentTotal ? `/${tx.installmentTotal}` : ""}` : "Lançamento fixo"} — ao salvar, vamos perguntar se a alteração vale só para este ou também para os seguintes.</p>}
        {!reconciled && (
          <>
            {!lockedSettle && <label className="check"><input type="checkbox" checked={settled} onChange={(e) => setSettled(e.target.checked)} name="settled" /> {rep.mode !== "unica" && !tx ? (kind === "despesa" ? "A primeira já foi paga" : "A primeira já foi recebida") : kind === "despesa" ? "Já foi pago" : "Já foi recebido"}</label>}
            <div className="grid2">
              {(!settled || tx) && <Field label={kind === "despesa" ? "Vencimento / data prevista" : "Data prevista"} required><Input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} name="plannedDate" required /></Field>}
              {settled && !lockedSettle && <Field label={kind === "despesa" ? "Data do pagamento" : "Data do recebimento"} required><Input type="date" value={settledDate} max={today()} onChange={(e) => setSettledDate(e.target.value)} name="settledDate" required /></Field>}
            </div>
          </>
        )}
        {(tx || rep.mode === "unica") && <AllocationField realm={realm} entityId={entityId} ownerType="transaction" ownerId={tx?.id} value={alloc} setValue={setAlloc} />}
        <AttachmentsField realm={realm} ownerType="transaction" ownerId={tx?.id} pending={pending} setPending={setPending} />
        <Field label="Observações"><Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} name="notes" /></Field>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><button type="submit" className="btn primary" disabled={busy} data-tx-save>{busy ? "Salvando…" : settled && !tx ? "Registrar" : "Salvar"}</button></div>
      </form>
    </Modal>
      {askScope && <ScopeDialog title="Aplicar a alteração a…" what={`Esta é uma ocorrência de série (${tx?.seriesKind === "parcelada" ? `parcela ${tx.installmentNo}/${tx.installmentTotal}` : "lançamento fixo"}). A alteração vale somente para ela ou também para as seguintes ainda planejadas?`} busy={busy} onClose={() => setAskScope(false)} onPick={(sc) => void submit(null, false, sc)} />}
    </>
  );
}

/** Pergunta (nunca automática) depois de salvar: criar regra, atualizar a existente ou não lembrar. O padrão não contém números nem datas. */
function LearnPrompt({ learn, categoryId, counterpartyId, entityId, onDone }: { learn: Learn; categoryId: string; counterpartyId: string | null; entityId: string; onDone: (msg?: string) => void }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const [pattern, setPattern] = useState(learn.proposal);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(newIdemKey());
  async function create() {
    setErr(null);
    try {
      await api("POST", `/api/${realm}/memo-rules`, { entityId, matchType: "contem", pattern, categoryId, counterpartyId, source: "aprendida" }, key.current);
      onDone(`Regra criada: descrições contendo "${pattern}" passam a sugerir esta categoria.`);
    } catch (e) { setErr(errMsg(e)); }
  }
  async function update() {
    if (!learn.existing) return;
    setErr(null);
    try {
      await api("PATCH", `/api/${realm}/memo-rules/${learn.existing.id}`, { version: learn.existing.version, categoryId, counterpartyId });
      onDone("Regra existente atualizada.");
    } catch (e) { setErr(errMsg(e)); }
  }
  return (
    <div className="learn" data-learn>
      <h4>Lembrar esta escolha?</h4>
      {err && <Notice kind="error">{err}</Notice>}
      {learn.existing && <p className="small muted">Já existe a regra "{learn.existing.pattern}" com outra categoria.</p>}
      <Field label="Quando a descrição contiver"><Input value={pattern} onChange={(e) => setPattern(e.target.value)} maxLength={120} name="learnPattern" /></Field>
      <p className="small muted">Só a descrição é memorizada — nunca valores ou datas.</p>
      <div className="row">
        <Btn onClick={() => void create()} disabled={pattern.trim().length < 2} data-learn-create>Criar regra</Btn>
        {learn.existing && <Btn variant="ghost" onClick={() => void update()} data-learn-update>Atualizar a regra existente</Btn>}
        <Btn variant="ghost" onClick={() => onDone()} data-learn-skip>Não lembrar</Btn>
      </div>
    </div>
  );
}
